import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { resolvePackerName, mapEadinStatus } from './receivedUtils';
import {
  applySearchPoToReceivingPo,
  buildQueueStatusParams,
  buildReceivingQueueFromApi,
  buildStubPoForInspector,
} from './receivingModel';
import { derivePoStatus, syncLineItemsReceivedFromBoxes } from './receivingStatus';
import {
  clearQueueCache,
  readQueueCache,
  writeQueueCache,
} from './receivingQueueCache';
import {
  createPoReceivedLogRequest,
  createPoOnHandCountRequest,
  createPoAmendCountRequest,
  applyInboundReceivedRequest,
  createBoxReceivedLogRequest,
  getPoReceivedLogsRequest,
  getReceivingQueueRequest,
  searchPoReceivedLogRequest,
} from '../../slices/boxReceivedSlice';

const ReceivingContext = createContext(null);

function pushActivity(po, user, summary, kind = 'system', detail) {
  return {
    ...po,
    activity: [
      { timestamp: new Date().toISOString(), user, summary, kind, detail },
      ...(po.activity || []),
    ].slice(0, 100),
  };
}

function sessionItems(po, box, found) {
  const line = po.lineItems.find((li) => li.sku === box.sku);
  return [
    {
      sku: box.sku,
      productName: line?.productName || box.sku,
      expectedUnits: box.expectedUnits,
      scannedUnits: found,
    },
  ];
}

/**
 * Box-unknown tags are informational (received sometime in the past on this
 * PO, owner unknown) — they are not duplicate exceptions. Filter them out of
 * the duplicates the store surfaces; keep receivedElsewhere and other-PO
 * received tags, which are real exceptions.
 */
export function filterBoxScanDuplicates(duplicates, classification) {
  const unknownHexes = new Set(
    (classification?.receivedBoxUnknown || []).map((t) => t.hex_epc),
  );
  return (duplicates || []).filter((d) => !unknownHexes.has(d.hexEpc));
}

export function applyCaptureScan(po, boxId, rawEpcs, kind, currentUser, apiLog) {
  const box = po.boxes.find((b) => b.id === boxId);
  if (!box) return po;
  const unique = Array.from(new Set(rawEpcs));
  // Server-authoritative scans carry scannedTotal; legacy paths fall back.
  const matched =
    apiLog?.scannedTotal ??
    apiLog?.total_matched ??
    apiLog?.amount_received ??
    unique.length;
  const duplicates = Array.isArray(apiLog?.duplicates) ? apiLog.duplicates : [];
  const hasDuplicates = duplicates.length > 0;
  // Don't wipe an existing scan when this session only found duplicates (matched = 0)
  const previousScanned =
    apiLog?.preserveScannedUnits != null && Number(apiLog.preserveScannedUnits) > 0
      ? Number(apiLog.preserveScannedUnits)
      : box.scannedUnits != null && Number(box.scannedUnits) > 0
        ? Number(box.scannedUnits)
        : null;
  const found =
    hasDuplicates && (matched == null || matched <= 0) && previousScanned != null
      ? previousScanned
      : matched;
  const scanState =
    found === box.expectedUnits ? 'complete' : found < box.expectedUnits ? 'short' : 'over';
  // Prefer duplicate badge when unresolved dupes are present (hand count later clears to override)
  const state = hasDuplicates ? 'duplicate_epc' : scanState;
  const autoReceived =
    apiLog?.autoReceived === true ||
    (apiLog?.boxReceived == null && scanState === 'complete' && !hasDuplicates);

  // A scan can never push receivedUnits above the server boxReceived, and
  // never below an existing hand-count / amended value.
  const floor =
    box.amendedUnits ??
    box.handCountUnits ??
    null;
  const receivedUnits =
    apiLog?.boxReceived != null
      ? Math.max(Number(apiLog.boxReceived), floor ?? 0)
      : autoReceived
        ? found
        : box.receivedUnits;

  const session = {
    id: `log-${apiLog?.id || Date.now()}`,
    logId: apiLog?.id,
    poId: po.id,
    boxId,
    timestamp: apiLog?.date_received || new Date().toISOString(),
    scannedBy: currentUser,
    rawReads: apiLog?.total_scanned ?? rawEpcs.length,
    uniqueEpcs: unique.length,
    matched: found,
    result: hasDuplicates ? 'duplicate_epc' : scanState,
    items: sessionItems(po, box, found),
    duplicates,
    ...(kind === 'rescan' ? { rescan: true } : {}),
  };

  let next = {
    ...po,
    boxes: po.boxes.map((b) =>
      b.id === boxId
        ? {
            ...b,
            scannedUnits: found,
            duplicateEpcs: duplicates,
            receivedUnits,
            state,
            scannedAt: new Date().toISOString(),
            scannedBy: currentUser,
          }
        : b,
    ),
    scanSessions: [
      ...po.scanSessions.map((sess) =>
        kind === 'rescan' && sess.boxId === boxId ? { ...sess, superseded: true } : sess,
      ),
      session,
    ],
  };

  if (autoReceived) {
    next = syncLineItemsReceivedFromBoxes(next);
  }

  if (hasDuplicates) {
    next = pushActivity(
      next,
      currentUser,
      `Box ${box.boxNumber}: ${duplicates.length} duplicate EPC${duplicates.length === 1 ? '' : 's'} — already received`,
      'exception',
    );
    next.exceptions = [
      ...next.exceptions.filter(
        (e) => !(e.boxId === boxId && e.kind === 'duplicate_epc' && !e.resolved),
      ),
      {
        kind: 'duplicate_epc',
        boxId,
        detail: `Duplicate EPC ${duplicates[0].hexEpc} — already received in ${duplicates[0].locationLabel}`,
        slackPushed: true,
        resolved: false,
        raisedAt: new Date().toISOString(),
      },
    ];
    if (scanState !== 'complete') {
      next.nextAction = `Hand-count Box ${box.boxNumber}`;
    } else {
      next.nextAction = `Resolve duplicate EPC on Box ${box.boxNumber}`;
    }
  } else if (scanState === 'complete') {
    next.exceptions = next.exceptions.map((e) =>
      e.boxId === boxId && !e.resolved ? { ...e, resolved: true } : e,
    );
    next = pushActivity(
      next,
      currentUser,
      `Box ${box.boxNumber} scanned ${found}/${box.expectedUnits}`,
      'scan',
    );
  } else {
    const label = scanState === 'short' ? 'SHORT' : 'OVER';
    next = pushActivity(
      next,
      currentUser,
      `Box ${box.boxNumber} scanned ${found}/${box.expectedUnits} — ${label}`,
      'exception',
    );
    if (!next.exceptions.some((e) => e.boxId === boxId && !e.resolved)) {
      next.exceptions = [
        ...next.exceptions,
        {
          kind: scanState === 'short' ? 'short_scan' : 'over_scan',
          boxId,
          detail: `Box ${box.boxNumber} scanned ${found}/${box.expectedUnits}`,
          slackPushed: true,
          resolved: false,
          raisedAt: new Date().toISOString(),
        },
      ];
    }
    next.nextAction = `Hand-count Box ${box.boxNumber}`;
  }

  next.status = derivePoStatus(next);
  if (next.status === 'reconciled' || next.status === 'fully_received') {
    next.receivedDate = next.receivedDate || new Date().toISOString().slice(0, 10);
    next.nextAction = next.status === 'reconciled' ? 'Close receipt' : 'Reconcile overrides';
  }
  return next;
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_REFRESHING':
      return { ...state, refreshing: action.refreshing };
    case 'HYDRATE':
      return {
        ...state,
        pos: action.pos,
        pagination: action.pagination || state.pagination,
        loading: false,
        refreshing: action.refreshing ?? false,
      };
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.currentUser };
    case 'UPDATE_POS':
      return { ...state, pos: action.pos };
    case 'PATCH_PO':
      return {
        ...state,
        pos: state.pos.map((p) => (p.id === action.poId ? action.po : p)),
      };
    case 'CLOSE_RECEIPT':
      return {
        ...state,
        pos: state.pos.map((po) =>
          action.poIds.includes(po.id)
            ? pushActivity(
                {
                  ...po,
                  eadinStatus: 'closed',
                  status: 'closed',
                  receiptClosedAt: po.receiptClosedAt || new Date().toISOString(),
                  nextAction: 'N/A',
                  receivedDate: po.receivedDate || new Date().toISOString().slice(0, 10),
                },
                state.currentUser,
                'Receipt closed',
              )
            : po,
        ),
      };
    case 'SET_PO_STATUS':
      return {
        ...state,
        pos: state.pos.map((po) => {
          if (!action.poIds.includes(po.id)) return po;
          const eadinStatus = mapEadinStatus(action.status);
          const next = {
            ...po,
            eadinStatus,
            ...(eadinStatus === 'closed'
              ? { status: 'closed', nextAction: 'N/A' }
              : {}),
          };
          return pushActivity(
            next,
            state.currentUser,
            `Status set to ${String(action.status).toUpperCase()}`,
          );
        }),
      };
    case 'ASSIGN_OWNER':
      return {
        ...state,
        pos: state.pos.map((po) =>
          action.poIds.includes(po.id) ? { ...po, assignee: action.owner } : po,
        ),
      };
    case 'ADD_NOTE':
      return {
        ...state,
        pos: state.pos.map((po) =>
          po.id === action.poId
            ? {
                ...po,
                notes: [
                  {
                    id: `note-${Date.now()}`,
                    text: action.text,
                    author: state.currentUser,
                    timestamp: new Date().toISOString(),
                  },
                  ...po.notes,
                ],
              }
            : po,
        ),
      };
    case 'FLAG_EXCEPTION':
      return {
        ...state,
        pos: state.pos.map((po) =>
          po.id === action.poId
            ? {
                ...po,
                exceptions: [
                  ...po.exceptions,
                  {
                    kind: 'manual_flag',
                    detail: action.detail,
                    slackPushed: true,
                    resolved: false,
                    raisedAt: new Date().toISOString(),
                  },
                ],
              }
            : po,
        ),
      };
    case 'OVERRIDE_BOX':
      return {
        ...state,
        pos: state.pos.map((po) => {
          if (po.id !== action.poId) return po;
          const box = po.boxes.find((b) => b.id === action.boxId);
          if (!box) return po;
          let next = syncLineItemsReceivedFromBoxes({
            ...po,
            boxes: po.boxes.map((b) =>
              b.id === action.boxId
                ? {
                    ...b,
                    // Hand count updates received only; keep RFID scanned value
                    handCountUnits: action.count,
                    receivedUnits: action.count,
                    state: 'override_received',
                  }
                : b,
            ),
          });
          next.status = derivePoStatus(next);
          return pushActivity(
            next,
            state.currentUser,
            `Box ${box.boxNumber} override received (${action.count} units)`,
          );
        }),
      };
    case 'MANUAL_RECEIVE_BOX':
      return {
        ...state,
        pos: state.pos.map((po) => {
          if (po.id !== action.poId) return po;
          const box = po.boxes.find((b) => b.id === action.boxId);
          if (!box) return po;
          let next = syncLineItemsReceivedFromBoxes({
            ...po,
            boxes: po.boxes.map((b) =>
              b.id === action.boxId
                ? {
                    ...b,
                    // Manual receive: received only; scanned stays unset
                    handCountUnits: action.count,
                    receivedUnits: action.count,
                    state: 'override_received',
                  }
                : b,
            ),
          });
          next.status = derivePoStatus(next);
          return pushActivity(
            next,
            state.currentUser,
            `Box ${box.boxNumber} manually received (${action.count} units)`,
          );
        }),
      };
    case 'RECONCILE':
      return {
        ...state,
        pos: state.pos.map((po) =>
          po.id === action.poId
            ? pushActivity(
                { ...po, status: 'reconciled', nextAction: 'Close receipt' },
                state.currentUser,
                'Receipt reconciled',
              )
            : po,
        ),
      };
    case 'AMEND_BOX':
      return {
        ...state,
        pos: state.pos.map((po) => {
          if (po.id !== action.poId) return po;
          const box = po.boxes.find((b) => b.id === action.boxId);
          if (!box) return po;
          const prevUnits =
            box.amendedUnits ??
            box.handCountUnits ??
            box.receivedUnits ??
            0;
          let next = syncLineItemsReceivedFromBoxes({
            ...po,
            boxes: po.boxes.map((b) =>
              b.id === action.boxId
                ? {
                    ...b,
                    amendedUnits: action.count,
                    receivedUnits: action.count,
                    amendedAt: new Date().toISOString(),
                    amendedBy: state.currentUser,
                    amendReason: action.reason,
                  }
                : b,
            ),
          });
          if (action.count !== box.expectedUnits) {
            next.exceptions = [
              ...next.exceptions,
              {
                kind: action.count < box.expectedUnits ? 'short_scan' : 'over_scan',
                boxId: action.boxId,
                detail: `Box ${box.boxNumber} amended to ${action.count}/${box.expectedUnits} — ${action.reason}`,
                slackPushed: true,
                resolved: false,
                raisedAt: new Date().toISOString(),
              },
            ];
          }
          next.status = derivePoStatus(next);
          return pushActivity(
            next,
            state.currentUser,
            `Box ${box.boxNumber} amended: ${prevUnits} → ${action.count}`,
            'amend',
            `Reason: ${action.reason}`,
          );
        }),
      };
    default:
      return state;
  }
}

export function ReceivingProvider({ children, onSessionSaved }) {
  const reduxDispatch = useDispatch();
  const authUser = useSelector((state) => state.auth?.currentUser);
  const packerName = resolvePackerName(authUser);

  const [state, dispatch] = useReducer(reducer, {
    pos: [],
    currentUser: packerName,
    loading: false,
    refreshing: false,
    pagination: null,
  });

  const posRef = useRef(state.pos);
  const currentUserRef = useRef(packerName);

  useEffect(() => {
    posRef.current = state.pos;
  }, [state.pos]);

  useEffect(() => {
    currentUserRef.current = packerName;
    dispatch({ type: 'SET_CURRENT_USER', currentUser: packerName });
  }, [packerName]);

  const loadQueueGenRef = useRef(0);

  const loadQueue = useCallback(async (filters = {}) => {
    const gen = ++loadQueueGenRef.current;

    // Instant paint: hydrate from the session cache first (stale-while-revalidate).
    // The network refresh below still runs and replaces the cached rows when done.
    const cached = readQueueCache(filters);
    if (cached) {
      dispatch({
        type: 'HYDRATE',
        pos: cached.pos,
        pagination: cached.pagination,
        refreshing: true,
      });
    } else {
      dispatch({ type: 'SET_LOADING', loading: true });
    }

    try {
      const search = filters.search ?? '';
      const searchTracking = filters.searchTracking === true;
      const statusFilter = filters.statusFilter ?? 'all';
      const showClosed = filters.showClosed ?? false;
      const page = filters.page ?? 1;
      const itemsPerPage = filters.itemsPerPage ?? 300;
      const statuses = buildQueueStatusParams(statusFilter, showClosed);
      const params = { page, itemsPerPage };
      if (search.trim()) params.search = search.trim();
      if (searchTracking) params.searchTracking = true;
      if (statuses.length > 0) params.statuses = statuses;

      const shipmentsData = await reduxDispatch(getReceivingQueueRequest(params)).unwrap();

      // A newer loadQueue started — discard this result
      if (gen !== loadQueueGenRef.current) return null;

      const result = buildReceivingQueueFromApi({
        shipmentsData,
        existingPos: filters.resetExisting && !cached ? [] : posRef.current,
        page,
      });
      dispatch({
        type: 'HYDRATE',
        pos: result.pos,
        pagination: result.pagination,
      });
      writeQueueCache(filters, result);
      return result;
    } catch (err) {
      const aborted =
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED' ||
        String(err?.message || '').toLowerCase().includes('abort') ||
        String(err?.error || '').toLowerCase().includes('abort');
      // Stale or canceled request — leave loading to the in-flight loadQueue
      if (aborted || gen !== loadQueueGenRef.current) {
        return null;
      }
      console.error('Failed to load receiving queue', err);
      dispatch({ type: 'SET_LOADING', loading: false });
      dispatch({ type: 'SET_REFRESHING', refreshing: false });
      return null;
    }
  }, [reduxDispatch]);

  /** @deprecated alias for callers that need queue hydration */
  const hydrate = useCallback(
    (filters) =>
      loadQueue({
        statusFilter: 'all',
        showClosed: true,
        ...filters,
      }),
    [loadQueue],
  );

  const refreshPo = useCallback(async (po) => {
    const logs = await reduxDispatch(
      getPoReceivedLogsRequest(po?.id ? { q: po.id } : {}),
    ).unwrap();
    const data = await reduxDispatch(
      searchPoReceivedLogRequest({ po_number: po.id }),
    ).unwrap();
    const shipmentsData = await reduxDispatch(
      getReceivingQueueRequest({
        page: 1,
        itemsPerPage: 300,
        search: po.poNumber || po.id,
        statuses: buildQueueStatusParams('all', true),
      }),
    ).unwrap();
    const freshQueuePo = buildReceivingQueueFromApi({
      shipmentsData,
      logs,
      existingPos: [],
      page: 1,
    }).pos.find((item) => item.id === po.id);
    const refreshed = applySearchPoToReceivingPo(freshQueuePo || po, data, logs);
    dispatch({ type: 'PATCH_PO', poId: po.id, po: refreshed });
    return refreshed;
  }, [reduxDispatch]);

  /** Ensure a PO is in the store with full boxes/lines (for Scan Log drawer). */
  const ensurePoLoaded = useCallback(
    async (poNumber) => {
      const existing = posRef.current.find((p) => p.id === poNumber);
      if (existing?.boxes?.length) {
        try {
          return await refreshPo(existing);
        } catch (err) {
          console.error('Failed to refresh PO for bulk receiving', err);
          return existing;
        }
      }
      try {
        const logs = await reduxDispatch(
          getPoReceivedLogsRequest({ q: poNumber }),
        ).unwrap();
        const data = await reduxDispatch(
          searchPoReceivedLogRequest({ po_number: poNumber }),
        ).unwrap();
        const loaded = applySearchPoToReceivingPo(
          buildStubPoForInspector(poNumber),
          data,
          logs,
        );
        const already = posRef.current.some((p) => p.id === loaded.id);
        if (already) {
          dispatch({ type: 'PATCH_PO', poId: loaded.id, po: loaded });
        } else {
          dispatch({ type: 'UPDATE_POS', pos: [loaded, ...posRef.current] });
        }
        return loaded;
      } catch (err) {
        console.error('Failed to load PO for inspector', err);
        return existing || null;
      }
    },
    [refreshPo, reduxDispatch],
  );

  const captureScan = useCallback(
    async (poId, boxId, scanPayload, kind = 'initial') => {
      const po = state.pos.find((p) => p.id === poId);
      if (!po?.poDbId) throw new Error('PO not loaded');
      const box = po.boxes?.find((b) => String(b.id) === String(boxId));
      const epcs = Array.isArray(scanPayload) ? scanPayload : scanPayload?.epcs || [];
      const matchedCount =
        scanPayload?.matchedCount != null ? scanPayload.matchedCount : new Set(epcs).size;
      const duplicates = Array.isArray(scanPayload?.duplicates) ? scanPayload.duplicates : [];
      const allowMixedSku = scanPayload?.allowMixedSku === true;
      const allowOverReceipt = scanPayload?.allowOverReceipt === true;
      const unique = Array.from(new Set(epcs));
      const numericBoxId = Number(boxId);
      const isRealBox =
        box?.inboundType === 'box' && Number.isInteger(numericBoxId) && numericBoxId > 0;

      let boxLog = null;
      let apiLog = null;

      if (isRealBox) {
        // Server-authoritative: the box endpoint classifies, stamps, logs,
        // and computes counts. 400s (mixed SKU, over-receipt) propagate to
        // the dialog so the operator sees the server's reason.
        if (unique.length > 0) {
          boxLog = await reduxDispatch(
            createBoxReceivedLogRequest({
              purchaseOrderId: po.poDbId,
              boxId: numericBoxId,
              dateReceived: new Date().toISOString(),
              epcHexTags: unique,
              allowMixedSku,
              allowOverReceipt,
            }),
          ).unwrap();
        }
      } else {
        // Containers / synthetic units keep the PO-level log as before.
        if (unique.length > 0) {
          apiLog = await reduxDispatch(
            createPoReceivedLogRequest({
              po_id: po.poDbId,
              date_received: new Date().toISOString(),
              epc_hex_tags: unique,
            }),
          ).unwrap();
        }
        const autoReceived =
          box?.expectedUnits > 0 && matchedCount === box.expectedUnits && duplicates.length === 0;
        if (autoReceived && box?.inboundType === 'container' && numericBoxId > 0) {
          await reduxDispatch(
            applyInboundReceivedRequest({
              purchaseOrderId: po.poDbId,
              boxId: numericBoxId,
              quantity: matchedCount,
              inboundType: 'container',
            }),
          ).unwrap();
        }
      }

      const refreshed = await refreshPo(po);
      const merged = applyCaptureScan(
        refreshed,
        boxId,
        unique,
        kind,
        currentUserRef.current,
        isRealBox
          ? {
              id: boxLog?.logId,
              total_matched: boxLog?.total_matched ?? 0,
              total_scanned: boxLog?.total_scanned ?? unique.length,
              scannedTotal: boxLog?.scannedTotal,
              amount_received: boxLog?.amountReceived,
              date_received: boxLog?.createdAt,
              autoReceived: boxLog?.autoReceived === true,
              boxReceived: boxLog?.boxReceived,
              duplicates: filterBoxScanDuplicates(
                boxLog?.duplicates,
                boxLog?.classification,
              ),
            }
          : {
              id: apiLog?.id,
              total_matched: matchedCount,
              total_scanned: scanPayload?.totalScanned ?? unique.length,
              amount_received: matchedCount,
              date_received: apiLog?.date_received,
              autoReceived:
                box?.expectedUnits > 0 &&
                matchedCount === box.expectedUnits &&
                duplicates.length === 0,
              duplicates: duplicates.length > 0 ? duplicates : apiLog?.duplicates || [],
            },
      );
      dispatch({ type: 'PATCH_PO', poId, po: merged });
      clearQueueCache();
      onSessionSaved?.();
      return merged;
    },
    [state.pos, refreshPo, onSessionSaved, reduxDispatch],
  );

  const captureAmendScan = useCallback(
    async (poId, boxId, rawEpcs) => {
      await captureScan(poId, boxId, rawEpcs, 'rescan');
    },
    [captureScan],
  );

  const closeReceipt = useCallback(
    async (poIds) => {
      dispatch({ type: 'CLOSE_RECEIPT', poIds });
      clearQueueCache();
      for (const id of poIds) {
        const po = posRef.current.find((p) => p.id === id);
        if (po?.poDbId) {
          try {
            await axios.post(`/api/v1/purchase-orders/${po.poDbId}/receipt-closed`);
          } catch (err) {
            console.error('Failed to close PO status', err);
          }
        }
      }
    },
    [],
  );

  const parseNumericBoxId = (boxId) => {
    const n = Number(boxId);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const resolveInboundId = (box) => {
    if (!box) return null;
    if (box.inboundType === 'container' && box.containerItemId) {
      return Number(box.containerItemId);
    }
    return parseNumericBoxId(box.id);
  };

  const overrideReceiveBox = useCallback(
    async (poId, boxId, count) => {
      const po = posRef.current.find((p) => p.id === poId);
      const box = po?.boxes?.find((b) => b.id === boxId);
      if (!po?.poDbId) throw new Error('PO not loaded');
      const numericBoxId = resolveInboundId(box);
      if (!numericBoxId) throw new Error('Valid inbound unit id is required');
      await reduxDispatch(
        createPoOnHandCountRequest({
          purchaseOrderId: po.poDbId,
          boxId: numericBoxId,
          quantity: count,
          source: 'hand_count',
          scannedQuantity: box?.scannedUnits ?? null,
          inboundType: box?.inboundType || undefined,
        }),
      ).unwrap();
      dispatch({ type: 'OVERRIDE_BOX', poId, boxId, count });
      clearQueueCache();
    },
    [reduxDispatch],
  );

  const manualReceiveBox = useCallback(
    async (poId, boxId, count) => {
      const po = posRef.current.find((p) => p.id === poId);
      const box = po?.boxes?.find((b) => b.id === boxId);
      if (!po?.poDbId) throw new Error('PO not loaded');
      const numericBoxId = resolveInboundId(box);
      if (!numericBoxId) throw new Error('Valid inbound unit id is required');
      await reduxDispatch(
        createPoOnHandCountRequest({
          purchaseOrderId: po.poDbId,
          boxId: numericBoxId,
          quantity: count,
          source: 'manual_receive',
          scannedQuantity: null,
          inboundType: box?.inboundType || undefined,
        }),
      ).unwrap();
      dispatch({ type: 'MANUAL_RECEIVE_BOX', poId, boxId, count });
      clearQueueCache();
    },
    [reduxDispatch],
  );

  const amendBoxUnits = useCallback(
    async (poId, boxId, count, reason) => {
      const po = posRef.current.find((p) => p.id === poId);
      const box = po?.boxes?.find((b) => b.id === boxId);
      if (!po?.poDbId) throw new Error('PO not loaded');
      const numericBoxId = resolveInboundId(box);
      if (!numericBoxId) throw new Error('Valid inbound unit id is required');
      const previousQuantity =
        box?.amendedUnits ??
        box?.handCountUnits ??
        box?.receivedUnits ??
        0;
      await reduxDispatch(
        createPoAmendCountRequest({
          purchaseOrderId: po.poDbId,
          boxId: numericBoxId,
          amendedQuantity: count,
          previousQuantity,
          reason,
          inboundType: box?.inboundType || undefined,
        }),
      ).unwrap();
      dispatch({ type: 'AMEND_BOX', poId, boxId, count, reason });
      clearQueueCache();
    },
    [reduxDispatch],
  );

  const value = useMemo(
    () => ({
      pos: state.pos,
      currentUser: state.currentUser,
      loading: state.loading,
      refreshing: state.refreshing,
      pagination: state.pagination,
      loadQueue,
      hydrate,
      refreshPo,
      ensurePoLoaded,
      captureScan,
      captureAmendScan,
      closeReceipt,
      assignOwner: (poIds, owner) => dispatch({ type: 'ASSIGN_OWNER', poIds, owner }),
      addNote: (poId, text) => dispatch({ type: 'ADD_NOTE', poId, text }),
      flagException: (poId, detail) => dispatch({ type: 'FLAG_EXCEPTION', poId, detail }),
      reconcile: (poId) => dispatch({ type: 'RECONCILE', poId }),
      overrideReceiveBox,
      manualReceiveBox,
      amendBoxUnits,
      attachDocument: (poId, name) => {
        const po = state.pos.find((p) => p.id === poId);
        if (!po) return;
        dispatch({
          type: 'PATCH_PO',
          poId,
          po: {
            ...po,
            attachments: [
              { id: `doc-${Date.now()}`, name, uploadedAt: new Date().toISOString() },
              ...po.attachments,
            ],
          },
        });
      },
    }),
    [
      state,
      loadQueue,
      hydrate,
      refreshPo,
      ensurePoLoaded,
      captureScan,
      captureAmendScan,
      closeReceipt,
      overrideReceiveBox,
      manualReceiveBox,
      amendBoxUnits,
    ],
  );

  return <ReceivingContext.Provider value={value}>{children}</ReceivingContext.Provider>;
}

export function useReceivingStore() {
  const ctx = useContext(ReceivingContext);
  if (!ctx) throw new Error('useReceivingStore must be used within ReceivingProvider');
  return ctx;
}
